/**
 * Находит ID страны по названию
 * @param {string} countryName - Название страны
 * @param {Array} countryOptions - Список стран из формы
 * @returns {string|null} - ID страны или null
 */
const findCountryId = (countryName, countryOptions) => {
  if (!countryName || !countryOptions || countryOptions.length === 0) {
    return null;
  }

  const normalized = countryName.toLowerCase().trim();

  // Точное совпадение
  const exactMatch = countryOptions.find(
    country => country.name.toLowerCase().trim() === normalized
  );
  if (exactMatch) {
    return exactMatch.id;
  }

  // Частичное совпадение
  const partialMatch = countryOptions.find(
    country => country.name.toLowerCase().includes(normalized) ||
               normalized.includes(country.name.toLowerCase())
  );
  if (partialMatch) {
    return partialMatch.id;
  }

  return null;
};

/**
 * Находит ID города по названию
 * @param {string} cityName - Название города
 * @param {Array} cityOptions - Список городов из формы
 * @returns {string|null} - ID города или null
 */
const findCityId = (cityName, cityOptions) => {
  if (!cityName || !cityOptions || cityOptions.length === 0) {
    return null;
  }

  const normalized = cityName.toLowerCase().trim();

  // Точное совпадение
  const exactMatch = cityOptions.find(
    city => city.name.toLowerCase().trim() === normalized
  );
  if (exactMatch) {
    return exactMatch.id;
  }

  // Частичное совпадение
  const partialMatch = cityOptions.find(
    city => city.name.toLowerCase().includes(normalized) ||
            normalized.includes(city.name.toLowerCase())
  );
  if (partialMatch) {
    return partialMatch.id;
  }

  return null;
};

/**
 * Находит профессию по названию
 * @param {string} professionName - Название профессии
 * @param {Array} professionsOptions - Список профессий из формы
 * @returns {Object|null} - Объект профессии или null
 */
const findProfession = (professionName, professionsOptions) => {
  if (!professionName || !professionsOptions || professionsOptions.length === 0) {
    return null;
  }

  const normalized = professionName.toLowerCase().trim();

  const exactMatch = professionsOptions.find(
    profession => profession.name.toLowerCase().trim() === normalized
  );
  if (exactMatch) return exactMatch;

  const partialMatch = professionsOptions.find(
    profession => {
      const profName = profession.name.toLowerCase();
      return profName.includes(normalized) || normalized.includes(profName);
    }
  );
  if (partialMatch) return partialMatch;

  console.warn(`Unable to map profession: "${professionName}". Try searching in database for similar names.`);
  return null;
};

/**
 * Normalizes contact value for better deduplication
 * @param {string} contactType - Type of contact (telegram, linkedin, github, etc.)
 * @param {string} value - Contact value
 * @returns {string} - Normalized value
 */
export const normalizeContactValue = (contactType, value) => {
  if (!value) return '';

  let normalized = value.trim().toLowerCase();

  // Telegram-specific normalization
  if (contactType === 'telegram') {
    // Remove @ prefix
    normalized = normalized.replace(/^@/, '');
    // Remove telegram.me/ or t.me/ prefix
    normalized = normalized.replace(/^(https?:\/\/)?(t\.me|telegram\.me)\//i, '');
    // Remove trailing slashes
    normalized = normalized.replace(/\/$/, '');
  }

  // LinkedIn-specific normalization
  if (contactType === 'linkedin') {
    // Extract username from URL
    normalized = normalized.replace(/^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company)\//i, '');
    normalized = normalized.replace(/\/$/, '');
  }

  // GitHub-specific normalization
  if (contactType === 'github') {
    normalized = normalized.replace(/^(https?:\/\/)?(www\.)?github\.com\//i, '');
    normalized = normalized.replace(/\/$/, '');
  }

  // Email normalization (lowercase already applied)
  if (contactType === 'email') {
    normalized = normalized.trim();
  }

  return normalized;
};

/**
 * Маппит контакты из AI на формат формы
 * @param {Array} aiContacts - Контакты от AI
 * @param {Array} toolsOptions - Список доступных tools
 * @returns {Array} - Контакты в формате формы
 */
const mapContacts = (aiContacts, toolsOptions) => {
  if (!aiContacts || !Array.isArray(aiContacts) || aiContacts.length === 0) {
    return { mapped: [], unmapped: [] };
  }

  // Побудуємо Map один раз: name → id (O(n)), потім лукап O(1)
  const toolsByName = new Map(
    toolsOptions.map(t => [t.name.toLowerCase(), t.id])
  );

  const seen = new Set();
  const mapped = [];
  const unmapped = [];

  for (const contact of aiContacts) {
    if (!contact.value) continue;

    const toolId = toolsByName.get(contact.type?.toLowerCase());
    if (!toolId) {
      unmapped.push({ type: contact.type, value: contact.value.trim() });
      continue;
    }

    const normalizedValue = normalizeContactValue(contact.type, contact.value);
    const dedupeKey = `${toolId}-${normalizedValue}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    mapped.push({
      tool_id: toolId,
      value: contact.value.trim(),
      is_primary: mapped.length === 0,
      is_verified: false
    });
  }

  return { mapped, unmapped };
};

/**
 * Маппит профессии из AI на ID профессий
 * @param {Array} aiProfessions - Профессии от AI (названия)
 * @param {Array} professionsOptions - Список профессий из формы
 * @returns {Array} - Массив ID профессий
 */
const mapProfessions = (aiProfessions, professionsOptions) => {
  if (!aiProfessions || !Array.isArray(aiProfessions) || aiProfessions.length === 0) {
    return { mapped: [], unmapped: [] };
  }

  const seen = new Set();
  const mapped = [];
  const unmapped = [];

  for (const profName of aiProfessions) {
    const profession = findProfession(profName, professionsOptions);
    if (!profession) {
      unmapped.push(profName);
      continue;
    }

    if (seen.has(profession.id)) continue;
    seen.add(profession.id);

    mapped.push({
      profession_id: profession.id,
      department_id: profession.department_id || null
    });
  }

  return { mapped, unmapped };
};

/**
 * Вычисляет возраст из даты рождения
 * @param {string} birthday - Дата рождения в формате YYYY-MM-DD
 * @returns {number|null} - Возраст или null
 */
const calculateAge = (birthday) => {
  if (!birthday) return null;

  try {
    const birthDate = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age > 0 && age < 120 ? age : null;
  } catch (error) {
    return null;
  }
};

/**
 * Основная функция маппинга данных от AI на поля формы
 * @param {Object} aiData - Данные от AI
 * @param {Object} formOptions - Опции формы (countries, cities, professions, tools)
 * @returns {Object} - Данные для заполнения формы
 */
export const mapResumeDataToForm = (aiData, formOptions) => {
  const {
    countryOptions = [],
    cityOptions = [],
    allCities = [],
    professionsOptions = [],
    toolsOptions = []
  } = formOptions;

  // Базовые поля
  const formData = {
    name: aiData.name || '',
    gender: aiData.gender || '',
    birthday: aiData.birthday || '',
    age: aiData.age || (aiData.birthday ? calculateAge(aiData.birthday) : ''),
    country_id: null,
    city_id: null
  };

  // Маппинг страны
  if (aiData.country) {
    formData.country_id = findCountryId(aiData.country, countryOptions);
  }

  // Маппинг города - используем allCities для поиска
  if (aiData.city) {
    const citiesForSearch = allCities.length > 0 ? allCities : cityOptions;
    formData.city_id = findCityId(aiData.city, citiesForSearch);
  }

  // Маппинг контактов
  const { mapped: contacts, unmapped: unmappedContacts } = mapContacts(aiData.contacts, toolsOptions);

  // Маппинг профессий
  const { mapped: professions, unmapped: unmappedProfessions } = mapProfessions(aiData.professions, professionsOptions);

  // Собираем unmapped items
  const unmapped = {
    professions: unmappedProfessions,
    contacts: unmappedContacts,
    country: aiData.country && !formData.country_id ? aiData.country : null,
    city: aiData.city && !formData.city_id ? aiData.city : null
  };

  return {
    formData,
    contacts,
    professions,
    unmapped
  };
};

/**
 * Находит ID языка по названию
 * @param {string} languageName - Название языка
 * @param {Array} languagesOptions - Список языков из формы
 * @returns {number|null} - ID языка или null
 */
const findLanguageId = (languageName, languagesOptions) => {
  if (!languageName || !languagesOptions || languagesOptions.length === 0) {
    return null;
  }

  const normalized = languageName.toLowerCase().trim();

  // Точное совпадение по name или code
  const exactMatch = languagesOptions.find(
    lang => lang.name?.toLowerCase().trim() === normalized ||
            lang.code?.toLowerCase().trim() === normalized
  );
  if (exactMatch) {
    return exactMatch.id;
  }

  // Частичное совпадение
  const partialMatch = languagesOptions.find(
    lang => lang.name?.toLowerCase().includes(normalized) ||
            normalized.includes(lang.name?.toLowerCase())
  );
  if (partialMatch) {
    return partialMatch.id;
  }

  return null;
};

/**
 * Находит ID уровня языка по названию
 * @param {string} levelName - Название уровня (e.g., "Advanced", "C1", "Intermediate")
 * @param {Array} languageLevelsOptions - Список уровней языков из формы
 * @returns {number|null} - ID уровня или null
 */
const findLanguageLevelId = (levelName, languageLevelsOptions) => {
  if (!levelName || !languageLevelsOptions || languageLevelsOptions.length === 0) {
    return null;
  }

  const normalized = levelName.toLowerCase().trim();
  // Normalize spaces for better matching (handle "B1-Intermediate", "B1 -Intermediate", etc.)
  const normalizedSpaces = normalized.replace(/\s*-\s*/g, ' - ').replace(/\s+/g, ' ');

  // Точное совпадение
  const exactMatch = languageLevelsOptions.find(
    level => {
      const levelNameNorm = level.name?.toLowerCase().trim().replace(/\s*-\s*/g, ' - ').replace(/\s+/g, ' ');
      const levelCodeNorm = level.code?.toLowerCase().trim().replace(/\s*-\s*/g, ' - ').replace(/\s+/g, ' ');
      return levelNameNorm === normalizedSpaces || levelCodeNorm === normalizedSpaces;
    }
  );
  if (exactMatch) {
    return exactMatch.id;
  }

  // Маппинг распространенных вариантов на названия из базы данных
  // База данных имеет: Beginner, Pre-intermediate, Intermediate, Upper-Intermediate, Advanced, Proficient
  const levelMap = {
    'beginner': ['a1', 'beginner', 'a1 - beginner', 'a1-beginner', 'starter', 'novice', 'elementary 1'],
    'pre-intermediate': ['a2', 'pre-intermediate', 'a2 - pre-intermediate', 'a2-pre-intermediate', 'elementary', 'basic', 'pre intermediate', 'preintermediate', 'elementary 2', 'lower intermediate'],
    'intermediate': ['b1', 'intermediate', 'b1 - intermediate', 'b1-intermediate', 'conversational', 'working proficiency', 'moderate'],
    'upper-intermediate': ['b2', 'upper-intermediate', 'b2 - upper-intermediate', 'b2-upper-intermediate', 'upper intermediate', 'upperintermediate', 'high intermediate', 'professional working proficiency', 'business english', 'business level'],
    'advanced': ['c1', 'advanced', 'c1 - advanced', 'c1-advanced', 'highly proficient', 'near native', 'full professional proficiency', 'very advanced'],
    'proficient': ['c2', 'mastery', 'c2 - mastery', 'c2-mastery', 'fluent', 'proficient', 'native', 'mother tongue', 'native speaker', 'bilingual', 'native-level', 'native level', 'c2-proficient']
  };

  // Ищем подходящий уровень
  for (const [dbLevelName, variants] of Object.entries(levelMap)) {
    // Check if the normalized input matches any of the variants
    if (variants.some(v => normalizedSpaces === v || normalizedSpaces.includes(v) || v.includes(normalizedSpaces))) {
      // Find the level in database by matching the key (database level name)
      const match = languageLevelsOptions.find(
        level => {
          const levelNameNorm = level.name?.toLowerCase().replace(/\s+/g, '-').trim();
          const levelCodeNorm = level.code?.toLowerCase().replace(/\s+/g, '-').trim();
          return levelNameNorm === dbLevelName || levelCodeNorm === dbLevelName ||
                 level.name?.toLowerCase() === dbLevelName.replace(/-/g, ' ');
        }
      );
      if (match) return match.id;
    }
  }

  // Log warning for unmapped levels to help improve mapping
  console.warn(`Unable to map language level: "${levelName}". Available levels in database:`,
    languageLevelsOptions.map(l => l.name)
  );
  return null;
};

/**
 * Маппит языки из AI на формат формы
 * @param {Array} aiLanguages - Языки от AI [{language: "English", level: "Advanced"}]
 * @param {Array} languagesOptions - Список языков
 * @param {Array} languageLevelsOptions - Список уровней
 * @returns {Array} - Языки в формате формы
 */
const mapLanguages = (aiLanguages, languagesOptions, languageLevelsOptions) => {
  if (!aiLanguages || !Array.isArray(aiLanguages) || aiLanguages.length === 0) {
    return { mapped: [], unmapped: [] };
  }

  const mapped = [];
  const unmapped = [];

  for (const aiLang of aiLanguages) {
    const languageId = findLanguageId(aiLang.language, languagesOptions);
    if (!languageId) {
      unmapped.push({ language: aiLang.language, level: aiLang.level || null });
      continue;
    }

    const levelId = aiLang.level ? findLanguageLevelId(aiLang.level, languageLevelsOptions) : null;

    mapped.push({
      language_id: languageId,
      level_id: levelId
    });
  }

  return { mapped, unmapped };
};

/**
 * Маппит tools из AI на tool_id
 * @param {Array} aiTools - Инструменты от AI (названия)
 * @param {Array} toolsOptions - Список всех tools из формы
 * @returns {Array} - Массив tool_id
 */
const mapTools = (aiTools, toolsOptions) => {
  if (!aiTools || !Array.isArray(aiTools) || toolsOptions.length === 0) {
    return { mapped: [], unmapped: [] };
  }

  // Exact-match map: lowercase name → tool (O(n) once)
  const toolsByName = new Map(
    toolsOptions.map(t => [t.name?.toLowerCase().trim(), t])
  );

  const seen = new Set();
  const mapped = [];
  const unmapped = [];

  for (const toolName of aiTools) {
    const normalized = toolName.toLowerCase().trim();

    // Exact match — O(1)
    let match = toolsByName.get(normalized);

    // Partial match fallback — O(n), only when exact fails
    if (!match) {
      match = toolsOptions.find(
        tool => tool.name?.toLowerCase().includes(normalized) ||
                normalized.includes(tool.name?.toLowerCase())
      );
    }

    if (match && !seen.has(match.id)) {
      seen.add(match.id);
      mapped.push(match.id);
    } else if (!match) {
      unmapped.push(toolName);
    }
  }

  return { mapped, unmapped };
};

/**
 * Маппинг данных от AI на поля формы Talent
 * Отличается от mapResumeDataToForm тем, что:
 * - Разделяет name на first_name и last_name
 * - Не включает birthday и age (отсутствуют в модели Talent)
 * - Добавляет поддержку languages, tools, work_experience, education, expected_rate, summary
 *
 * @param {Object} aiData - Данные от AI
 * @param {Object} formOptions - Опции формы (countries, cities, professions, tools, languages, languageLevels)
 * @returns {Object} - Данные для заполнения формы Talent
 */
export const mapResumeDataToTalentForm = (aiData, formOptions) => {
  const {
    countryOptions = [],
    cityOptions = [],
    allCities = [],
    professionsOptions = [],
    toolsOptions = [],
    contactToolsOptions = [],
    languagesOptions = [],
    languageLevelsOptions = []
  } = formOptions;

  // Обрабатываем имя: AI может вернуть либо first_name/last_name (новый промпт),
  // либо name (старый промпт) - нужно поддерживать оба варианта
  let firstName = '';
  let lastName = '';

  if (aiData.first_name || aiData.last_name) {
    // Новый формат: AI уже разделил имя
    firstName = aiData.first_name || '';
    lastName = aiData.last_name || '';
  } else if (aiData.name) {
    // Старый формат: AI вернул полное имя, разделяем вручную
    const nameParts = aiData.name.trim().split(/\s+/);
    if (nameParts.length === 1) {
      firstName = nameParts[0];
    } else if (nameParts.length >= 2) {
      firstName = nameParts[0];
      lastName = nameParts.slice(1).join(' ');
    }
  }

  // Базовые поля для Talent
  const formData = {
    first_name: firstName,
    last_name: lastName,
    country_id: null,
    city_id: null
  };

  // Маппинг страны
  if (aiData.country) {
    formData.country_id = findCountryId(aiData.country, countryOptions);
  }

  // Маппинг города - используем allCities для поиска
  if (aiData.city) {
    const citiesForSearch = allCities.length > 0 ? allCities : cityOptions;
    formData.city_id = findCityId(aiData.city, citiesForSearch);
  }

  // Маппинг контактов - используем contactToolsOptions если есть, иначе toolsOptions
  const { mapped: contacts, unmapped: unmappedContacts } = mapContacts(aiData.contacts, contactToolsOptions.length > 0 ? contactToolsOptions : toolsOptions);

  // Маппинг профессий
  const { mapped: professions, unmapped: unmappedProfessions } = mapProfessions(aiData.professions, professionsOptions);

  // Маппинг языков
  const { mapped: languages, unmapped: unmappedLanguages } = mapLanguages(aiData.languages, languagesOptions, languageLevelsOptions);

  // Маппинг инструментов
  const { mapped: tools, unmapped: unmappedTools } = mapTools(aiData.tools, toolsOptions);

  // Маппинг work experience в CV формат
  const workExperience = (aiData.work_experience || []).map(exp => ({
    company_name: exp.company || '',
    cv_type: 'experiences',
    specialisation: exp.position || '',
    start_date: exp.start_date || null,
    end_date: exp.end_date === 'Present' ? null : (exp.end_date || null),
    description: exp.description || '',
    professions: [],
    industries: [],
    subIndustries: []
  }));

  // Маппинг education в CV формат
  const education = (aiData.education || []).map(edu => {
    // Нормализуем start_date
    let startDate = null;
    if (edu.start_date) {
      // Если дата уже в формате YYYY-MM-DD, используем как есть
      if (/^\d{4}-\d{2}-\d{2}$/.test(edu.start_date)) {
        startDate = edu.start_date;
      }
      // Если дата в формате YYYY-MM, добавляем -01
      else if (/^\d{4}-\d{2}$/.test(edu.start_date)) {
        startDate = `${edu.start_date}-01`;
      }
      // Если только год YYYY, добавляем -01-01
      else if (/^\d{4}$/.test(edu.start_date)) {
        startDate = `${edu.start_date}-01-01`;
      }
      // Иначе используем как есть
      else {
        startDate = edu.start_date;
      }
    }

    // Нормализуем end_date
    let endDate = null;
    if (edu.end_date && edu.end_date !== 'Present') {
      // Если дата уже в формате YYYY-MM-DD, используем как есть
      if (/^\d{4}-\d{2}-\d{2}$/.test(edu.end_date)) {
        endDate = edu.end_date;
      }
      // Если дата в формате YYYY-MM, добавляем последний день месяца
      else if (/^\d{4}-\d{2}$/.test(edu.end_date)) {
        const [year, month] = edu.end_date.split('-');
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        endDate = `${edu.end_date}-${lastDay.toString().padStart(2, '0')}`;
      }
      // Если только год YYYY, добавляем -12-31
      else if (/^\d{4}$/.test(edu.end_date)) {
        endDate = `${edu.end_date}-12-31`;
      }
      // Иначе используем как есть
      else {
        endDate = edu.end_date;
      }
    }

    return {
      company_name: edu.institution || '',
      cv_type: 'education',
      specialisation: edu.degree || '',
      start_date: startDate,
      end_date: endDate,
      description: '',
      professions: [],
      industries: [],
      subIndustries: []
    };
  });

  // Маппинг certifications в CV формат (тип: courses)
  const certifications = (aiData.certifications || []).map(cert => {
    // Определяем start_date из поля date
    let startDate = null;
    if (cert.date) {
      // Если дата в формате YYYY-MM, используем как есть
      if (/^\d{4}-\d{2}$/.test(cert.date)) {
        startDate = `${cert.date}-01`;
      }
      // Если дата в формате YYYY, добавляем 01-01
      else if (/^\d{4}$/.test(cert.date)) {
        startDate = `${cert.date}-01-01`;
      }
      // Иначе пытаемся использовать как есть
      else {
        startDate = cert.date;
      }
    }

    return {
      company_name: cert.provider || '',
      cv_type: 'courses',
      specialisation: cert.name || '',
      start_date: startDate,
      end_date: null, // Сертификаты обычно имеют только дату завершения, не период
      description: '',
      professions: [],
      industries: [],
      subIndustries: []
    };
  });

  // Объединяем work experience, education и certifications в cv
  const cv = [...workExperience, ...education, ...certifications];

  // Парсим expected_rate → { amount, currency }
  const expected_rate = aiData.expected_rate ? parseExpectedRate(aiData.expected_rate) : null;

  // Собираем unmapped items
  const unmapped = {
    tools: unmappedTools,
    professions: unmappedProfessions,
    languages: unmappedLanguages,
    contacts: unmappedContacts,
    country: aiData.country && !formData.country_id ? aiData.country : null,
    city: aiData.city && !formData.city_id ? aiData.city : null
  };

  return {
    formData,
    contacts,
    professions,
    languages,
    tools,
    cv,
    summary: aiData.summary || null,
    expected_rate,
    unmapped
  };
};

/**
 * Парсит строку с expected_rate и конвертирует в месячную ставку
 * @param {string} rateString - Строка вида "$8333/month", "$100k/year" або "50$/hour"
 * @returns {{amount: number, currency: string|null}|null}
 */
const parseExpectedRate = (rateString) => {
  if (!rateString || typeof rateString !== 'string') return null;

  const numMatch = rateString.match(/[\d.]+/);
  if (!numMatch) return null;

  let amount = parseFloat(numMatch[0]);

  if (rateString.toLowerCase().includes('k')) {
    amount *= 1000;
  }

  // Конвертация в месячную ставку (fallback если AI вернул не monthly)
  const lower = rateString.toLowerCase();
  if (lower.includes('year')) {
    amount /= 12;
  } else if (lower.includes('hour') || lower.includes('hr')) {
    amount *= 173; // ~2080 working hours / 12 months
  }
  // "month" или без единицы → уже месячная

  const currencyMatch = rateString.match(/[$€£₴]/);

  return amount > 0 ? { amount: Math.round(amount), currency: currencyMatch ? currencyMatch[0] : null } : null;
};

/**
 * Создает читаемый отчет о распознанных данных
 * @param {Object} aiData - Данные от AI
 * @returns {string} - Текстовый отчет
 */
export const createParseReport = (aiData) => {
  const lines = [];

  lines.push('Recognized data:');
  lines.push(`- Name: ${aiData.name || 'Not found'}`);
  lines.push(`- Gender: ${aiData.gender || 'Not found'}`);
  lines.push(`- Birthday: ${aiData.birthday || 'Not found'}`);
  lines.push(`- Age: ${aiData.age || 'Not found'}`);
  lines.push(`- Country: ${aiData.country || 'Not found'}`);
  lines.push(`- City: ${aiData.city || 'Not found'}`);
  lines.push(`- Professions: ${aiData.professions?.length ? aiData.professions.join(', ') : 'Not found'}`);
  lines.push(`- Contacts: ${aiData.contacts?.length || 0} found`);

  return lines.join('\n');
};
